import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import AnimateOnScroll from './AnimateOnScroll';
import { contactFormSection } from '../content/landingContent';
import { Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactFormSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRoleChange = (value) => {
    setFormData({
      ...formData,
      role: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      setIsSuccess(true);
      toast({
        title: "Message sent!",
        description: contactFormSection.successMessage,
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          role: '',
          message: ''
        });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: contactFormSection.errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-slate-100 rounded-full opacity-30 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-stone-100 rounded-full opacity-30 blur-3xl"></div>
      
      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
        <AnimateOnScroll>
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-semibold text-stone-900 mb-6 tracking-tight">
              {contactFormSection.headline}
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              {contactFormSection.subheadline}
            </p>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay={200}>
          <div className="bg-gradient-to-br from-white to-stone-50 rounded-2xl shadow-xl border border-stone-200 p-8 lg:p-12">
            {isSuccess ? (
              <div className="text-center py-12 space-y-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-stone-900 mb-2">Thank you!</h3>
                  <p className="text-stone-600">{contactFormSection.successMessage}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-stone-900">
                      {contactFormSection.formFields.name.label}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={contactFormSection.formFields.name.placeholder}
                      className="border-stone-300 focus:border-slate-500 focus:ring-slate-500"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-stone-900">
                      {contactFormSection.formFields.email.label}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={contactFormSection.formFields.email.placeholder}
                      className="border-stone-300 focus:border-slate-500 focus:ring-slate-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Company */}
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-stone-900">
                      {contactFormSection.formFields.company.label}
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={contactFormSection.formFields.company.placeholder}
                      className="border-stone-300 focus:border-slate-500 focus:ring-slate-500"
                    />
                  </div>

                  {/* Role */}
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-stone-900">
                      {contactFormSection.formFields.role.label}
                    </Label>
                    <Select value={formData.role} onValueChange={handleRoleChange}>
                      <SelectTrigger className="border-stone-300 focus:border-slate-500 focus:ring-slate-500">
                        <SelectValue placeholder={contactFormSection.formFields.role.placeholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {contactFormSection.formFields.role.options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-stone-900">
                    {contactFormSection.formFields.message.label}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={contactFormSection.formFields.message.placeholder}
                    className="border-stone-300 focus:border-slate-500 focus:ring-slate-500 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full bg-slate-600 hover:bg-slate-700 text-white shadow-md hover:shadow-lg transition-all duration-300 group"
                  >
                    {isSubmitting ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        {contactFormSection.submitButton}
                        <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ContactFormSection;